import { useState, useEffect } from 'react';
import { useWorkNoteStore } from '../../../store/workNoteStore';
import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Pin, Clock, Search, FileText, Plus, Trash2 } from 'lucide-react';
import { Input } from '../../../components/Input';
import { Textarea } from '../../../components/Textarea';
import { Switch } from '../../../components/Switch';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/Dialog';
import toast from 'react-hot-toast';

const EMPTY_FORM = { title: '', content: '', tags: '', isPinned: false };

export function Notes() {
    const { notes, isLoading, fetchNotes, createNote, deleteNote, updateNote } = useWorkNoteStore();
    const [search, setSearch] = useState('');
    const [isFormOpen, setFormOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        fetchNotes();
    }, []);

    const filteredNotes = notes.filter(n =>
        n.title.toLowerCase().includes(search.toLowerCase()) ||
        n.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase()))
    );

    const openCreate = () => { setForm(EMPTY_FORM); setErrors({}); setFormOpen(true); };

    const handleSubmit = async () => {
        const next: Record<string, string> = {};
        if (!form.title.trim()) next.title = 'Title is required';
        if (!form.content.trim()) next.content = 'Content is required';
        setErrors(next);
        if (Object.keys(next).length) return;
        setSaving(true);
        try {
            await createNote({
                title: form.title.trim(),
                content: form.content.trim(),
                isPinned: form.isPinned,
                tags: form.tags.split(',').map(t => t.trim()).filter(Boolean),
            });
            toast.success('Note created');
            setFormOpen(false);
        } catch (e) {
            toast.error('Failed to create note');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await deleteNote(deleteTarget.id);
            toast.success('Note deleted');
            setDeleteTarget(null);
        } catch (e) {
            toast.error('Failed to delete note');
        } finally {
            setDeleting(false);
        }
    };

    const togglePin = async (id: string, currentPin: boolean) => {
        try {
            await updateNote(id, { isPinned: !currentPin });
            toast.success(currentPin ? 'Note unpinned' : 'Note pinned');
        } catch (e) {
            toast.error('Failed to update note');
        }
    };

    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Notes</h1>
                    <p className="text-muted-foreground">Quick scratchpads and meeting notes.</p>
                </div>
                <Button variant="primary" onClick={openCreate}>
                    <Plus className="w-4 h-4 mr-2" />
                    New Note
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 flex-1 min-h-0">
                <div className="md:col-span-1 space-y-4 flex flex-col hidden md:flex">
                    <Card className="flex-1 flex flex-col">
                        <CardHeader>
                            <h3 className="text-foreground font-display text-card-title">Tags</h3>
                        </CardHeader>
                        <CardContent className="space-y-2 flex-1 overflow-y-auto">
                            <div className="flex flex-wrap gap-2">
                                <Badge variant="secondary" className="cursor-pointer hover:bg-secondary-foreground hover:text-secondary">planning</Badge>
                                <Badge variant="secondary" className="cursor-pointer hover:bg-secondary-foreground hover:text-secondary">meeting</Badge>
                                <Badge variant="secondary" className="cursor-pointer hover:bg-secondary-foreground hover:text-secondary">urgent</Badge>
                                <Badge variant="secondary" className="cursor-pointer hover:bg-secondary-foreground hover:text-secondary">ideas</Badge>
                                <Badge variant="secondary" className="cursor-pointer hover:bg-secondary-foreground hover:text-secondary">personal</Badge>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="md:col-span-3 flex flex-col space-y-4 min-h-0">
                    <Card className="flex-1 flex flex-col min-h-0 bg-card">
                        <CardHeader className="shrink-0 border-b border-border bg-secondary/10">
                            <div className="flex justify-between items-center">
                                <div className="relative w-full max-w-md">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                    <Input 
                                        placeholder="Search notes..." 
                                        className="pl-9 w-full bg-background"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-4 flex-1 overflow-auto bg-background/50">
                            {isLoading ? (
                                <div className="p-8 text-center text-muted-foreground">Loading...</div>
                            ) : filteredNotes.length === 0 ? (
                                <div className="p-12 text-center text-muted-foreground border-2 border-dashed border-border rounded-lg">
                                    <FileText className="w-12 h-12 mx-auto mb-4 opacity-20" />
                                    <p>No notes found. Create your first note.</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {filteredNotes.map(n => (
                                        <Card key={n.id} className="h-48 flex flex-col cursor-pointer hover:shadow-md transition-shadow group">
                                            <CardHeader className="p-3 pb-0 shrink-0">
                                                <div className="flex justify-between items-start">
                                                    <h4 className="text-foreground line-clamp-1 flex-1 pr-2 text-sm font-semibold">{n.title}</h4>
                                                    <div className="flex items-center gap-1 shrink-0">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); togglePin(n.id, n.isPinned); }}
                                                            className={`p-1 rounded hover:bg-secondary transition-colors ${n.isPinned ? 'text-primary' : 'text-muted-foreground/50 opacity-0 group-hover:opacity-100'}`}
                                                        >
                                                            <Pin className="w-4 h-4" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); setDeleteTarget({ id: n.id, title: n.title }); }}
                                                            className="p-1 rounded hover:bg-secondary text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-destructive transition-colors"
                                                            aria-label="Delete note"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="text-xs text-muted-foreground flex items-center mt-1">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    {new Date(n.updatedAt).toLocaleDateString()}
                                                </div>
                                            </CardHeader>
                                            <CardContent className="p-3 pt-2 flex-1 flex flex-col justify-between overflow-hidden">
                                                <p className="text-sm text-muted-foreground line-clamp-4 flex-1">
                                                    {n.content}
                                                </p>
                                                {n.tags && n.tags.length > 0 && (
                                                    <div className="flex gap-1 flex-wrap mt-2 shrink-0">
                                                        {n.tags.slice(0, 3).map(tag => (
                                                            <span key={tag} className="text-xs px-1.5 py-0.5 bg-secondary text-muted-foreground rounded">
                                                                #{tag}
                                                            </span>
                                                        ))}
                                                        {n.tags.length > 3 && (
                                                            <span className="text-xs px-1.5 py-0.5 bg-secondary text-muted-foreground rounded">
                                                                +{n.tags.length - 3}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Create note modal */}
            <Dialog open={isFormOpen} onOpenChange={setFormOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>New Note</DialogTitle>
                        <DialogDescription>Capture a quick note or meeting summary.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Title</label>
                            <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                                placeholder="e.g. Q4 planning notes" invalid={!!errors.title} />
                            {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Content</label>
                            <Textarea rows={6} value={form.content} onChange={(e) => setForm(f => ({ ...f, content: e.target.value }))}
                                placeholder="Write your note…" invalid={!!errors.content} />
                            {errors.content && <p className="text-xs text-destructive">{errors.content}</p>}
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-sm font-medium text-foreground">Tags <span className="text-muted-foreground font-normal">(comma-separated)</span></label>
                            <Input value={form.tags} onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                                placeholder="planning, roadmap" />
                        </div>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <Switch checked={form.isPinned} onCheckedChange={(v) => setForm(f => ({ ...f, isPinned: v }))} />
                            <span className="text-sm font-medium text-foreground">Pin this note</span>
                        </label>
                    </div>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setFormOpen(false)} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleSubmit} isLoading={saving}>Create Note</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <DialogContent className="max-w-[440px]">
                    <DialogHeader>
                        <DialogTitle>Delete note</DialogTitle>
                        <DialogDescription>
                            Delete <span className="font-medium text-foreground">{deleteTarget?.title}</span>? This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete} isLoading={deleting}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
